import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get } from 'lodash';
import IconProtectionPlan from 'components/icons/iconProtectionPlan';
import IconPetProtector from 'components/icons/iconPetProtector';
import withCheckoutUtils from '../checkout/withCheckoutUtils';
import ProtectionPlansItem from './ProtectionPlansItem';

const ProtectionPlans = ({
  cartItemId,
  checkoutUtilActions,
  eligiblePlans = [],
  forPage = 'cart',
  onAdd,
  onRemove,
  onSelect,
  selectedPlan = null,
  showImages = false,
  showInactiveProtectionPlan = false,
}) => {
  const [state, setState] = useState({
    error: null,
    isRemoveLoading: false,
    isStandardLoading: false,
    isPetsAndKidsLoading: false,
  });

  if (!eligiblePlans || !eligiblePlans.length) return null;

  let isStandard = false;
  let isPetsAndKids = false;

  if (selectedPlan) {
    if (eligiblePlans[0]) {
      isStandard = eligiblePlans[0].plan_id === selectedPlan.plan_id;
    }
    if (eligiblePlans[1]) {
      isPetsAndKids = eligiblePlans[1].plan_id === selectedPlan.plan_id;
    }
  }

  const onClickFirstPlan = async () => {
    if (!['cart', 'checkout'].includes(forPage)) {
      if (onSelect) {
        onSelect(eligiblePlans[0].plan_id);
      }
      if (onAdd) {
        onAdd(eligiblePlans[0].plan_id);
      }
    } else {
      try {
        const isAdd = !isStandard;
        setState({ ...state, isStandardLoading: true });
        const {
          success,
          errors,
        } = await checkoutUtilActions.toggleCartItemProtectionPlan(
          cartItemId,
          isAdd ? eligiblePlans[0].plan_id : null,
          !!isAdd
        );
        if (success) {
          await checkoutUtilActions.getCart();
          setState({
            ...state,
            isStandardLoading: false,
          });
          if (isAdd) {
            if (onAdd) onAdd();
          } else if (onRemove) onRemove();
        } else {
          setState({
            ...state,
            isStandardLoading: false,
            error: get(errors, '0.error', 'Error occurred. Please try again.'),
          });
        }
      } catch (e) {
        setState({
          ...state,
          isStandardLoading: false,
          error: 'Error occurred. Please try again.',
        });
      }
    }
  };

  const onClickSecondPlan = async () => {
    if (!['cart', 'checkout'].includes(forPage)) {
      if (onSelect) {
        onSelect(eligiblePlans[1].plan_id);
      }
      if (onAdd) {
        onAdd(eligiblePlans[1].plan_id);
      }
    } else {
      try {
        const isAdd = !isPetsAndKids;
        setState({ ...state, isPetsAndKidsLoading: true });
        const {
          success,
          errors,
        } = await checkoutUtilActions.toggleCartItemProtectionPlan(
          cartItemId,
          isAdd ? eligiblePlans[1].plan_id : null,
          !!isAdd
        );
        if (success) {
          await checkoutUtilActions.getCart();
          setState({
            ...state,
            isPetsAndKidsLoading: false,
          });
          if (isAdd) {
            if (onAdd) onAdd();
          } else if (onRemove) onRemove();
        } else {
          setState({
            ...state,
            isPetsAndKidsLoading: false,
            error: get(errors, '0.error', 'Error occurred. Please try again.'),
          });
        }
      } catch (e) {
        setState({
          ...state,
          isPetsAndKidsLoading: false,
          error: 'Error occurred. Please try again.',
        });
      }
    }
  };

  return (
    <>
      <div
        className={classNames('flex flex-col items-center', {
          'bg-gray-light8 md:bg-transparent': !showImages,
        })}
      >
        <div
          className={classNames(
            'flex flex-col lg:flex-row lg:justify-center max-lg:gap-6',
            {
              'gap-6 mt-4': showImages,
              'max-lg:!gap-0': !eligiblePlans[1] && showInactiveProtectionPlan,
              'w-full': !showImages,
            }
          )}
        >
          {eligiblePlans[0] && (
            <ProtectionPlansItem
              ctaButtonChildren={
                isStandard ? (
                  <span className="flex">
                    <IconProtectionPlan /> {eligiblePlans[0].plan_name} Added
                  </span>
                ) : isPetsAndKids ? (
                  `Downgrade to ${eligiblePlans[0].plan_name}`
                ) : (
                  `+ Add ${eligiblePlans[0].plan_name}`
                )
              }
              ctaId="standard-plan-btn"
              icon={<IconProtectionPlan />}
              image="https://joybird2.imgix.net/public_img/life-happens.jpg"
              isLoading={state.isStandardLoading}
              isPetsAndKids={isPetsAndKids}
              isStandard={isStandard}
              onClick={onClickFirstPlan}
              planDescription={eligiblePlans[0].plan_description}
              planName={eligiblePlans[0].plan_name}
              planPrice={eligiblePlans[0].plan_price}
              showImages={showImages}
            />
          )}

          {eligiblePlans[1] ? (
            <ProtectionPlansItem
              ctaButtonChildren={
                isPetsAndKids ? (
                  <span className="flex">
                    <IconProtectionPlan /> {eligiblePlans[1].plan_name} Added
                  </span>
                ) : isStandard ? (
                  `Upgrade to ${eligiblePlans[1].plan_name}`
                ) : (
                  `+ Add ${eligiblePlans[1].plan_name}`
                )
              }
              ctaId="pets-and-kids-plan-btn"
              icon={<IconPetProtector />}
              image="https://joybird2.imgix.net/public_img/pet-protector.jpg"
              isLoading={state.isPetsAndKidsLoading}
              isMostPopular
              isPetsAndKids={isPetsAndKids}
              isStandard={isStandard}
              onClick={onClickSecondPlan}
              planDescription={eligiblePlans[1].plan_description}
              planName={eligiblePlans[1].plan_name}
              planPrice={eligiblePlans[1].plan_price}
              showImages={showImages}
            />
          ) : showInactiveProtectionPlan ? (
            <div
              className={classNames(
                'relative rounded border-0 group flex-1 flex',
                {
                  'bg-white': showImages,
                  'bg-gray-light8 md:bg-white': !showImages,
                }
              )}
            >
              <div
                className={classNames(
                  'min-h-[15rem] p-4 md:p-6 text-sm text-center flex flex-col justify-center items-center gap-4 flex-1',
                  {
                    'rounded-none border border-solid border-gray-light7 h-full bg-white mx-4 mb-4 mt-0 md:bg-transparent md:mx-0 md:mb-0': !showImages,
                  }
                )}
              >
                <IconPetProtector inactive />

                <div className="max-w-[130px]">
                  This product is not eligible for our Pet Protector Plan.
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {state.error ? (
        <div className="text-red px-4 py-2">{state.error}</div>
      ) : null}
    </>
  );
};

ProtectionPlans.propTypes = {
  cartItemId: PropTypes.string,
  checkoutUtilActions: PropTypes.shape({
    getCart: PropTypes.func,
    toggleCartItemProtectionPlan: PropTypes.func,
  }),
  eligiblePlans: PropTypes.array,
  forPage: PropTypes.oneOf([
    'cart',
    'checkout',
    'PDP',
    'UserDashboardPP',
    'UserDashboardOrdersPage',
  ]),
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  onSelect: PropTypes.func,
  selectedPlan: PropTypes.object,
  showImages: PropTypes.bool,
  showInactiveProtectionPlan: PropTypes.bool,
};

export default withCheckoutUtils(ProtectionPlans);
